import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Select } from '@components';
import { UsageSummary, UsageRecord } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface UsageDashboardProps {
  summary: UsageSummary | null;
  records: UsageRecord[];
  loading?: boolean;
  onDateRangeChange?: (range: string) => void;
}

export const UsageMetrics: React.FC<UsageDashboardProps> = ({
  summary,
  records,
  loading = false,
  onDateRangeChange,
}) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState('current');

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    onDateRangeChange?.(value);
  };

  const formatCost = (cost: string) => {
    return `€${parseFloat(cost).toFixed(2)}`;
  };

  const formatCallDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Parse date string without timezone conversion issues
  const parseDateString = (dateString: string): Date => {
    // Extract date part from ISO datetime strings (e.g., "2025-12-20T23:59:59.999999+00:00" -> "2025-12-20")
    const dateOnlyMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnlyMatch) {
      const [year, month, day] = dateOnlyMatch[1].split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateString);
  };

  const formatPeriodDate = (dateString: string): string => {
    const date = parseDateString(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Prepare chart data from records - group by date and sum by usage type
  const chartDataMap = new Map<
    string,
    { date: string; call_minutes: number; sms: number; web_search: number; phone_number: number }
  >();

  records.forEach(record => {
    const dateKey = new Date(record.created).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const cost = parseFloat(record.total_cost || '0');

    if (!chartDataMap.has(dateKey)) {
      chartDataMap.set(dateKey, {
        date: dateKey,
        call_minutes: 0,
        sms: 0,
        web_search: 0,
        phone_number: 0,
      });
    }

    const dayData = chartDataMap.get(dateKey)!;

    if (record.usage_type === 'call_minutes') {
      dayData.call_minutes += cost;
    } else if (record.usage_type === 'sms') {
      dayData.sms += cost;
    } else if (record.usage_type === 'web_search') {
      dayData.web_search += cost;
    } else if (record.usage_type === 'phone_number') {
      dayData.phone_number += cost;
    }
  });

  const chartData = Array.from(chartDataMap.values())
    .map(day => ({
      date: day.date,
      call_minutes: Math.round(day.call_minutes * 100) / 100,
      sms: Math.round(day.sms * 100) / 100,
      web_search: Math.round(day.web_search * 100) / 100,
      phone_number: Math.round(day.phone_number * 100) / 100,
    }))
    .sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('billing.usageDashboard')}
        </h2>
        <div className="flex gap-4">
          <Select
            value={dateRange}
            onChange={value => {
              const stringValue = Array.isArray(value) ? value[0] : value;
              handleDateRangeChange(stringValue);
            }}
            options={[
              { value: 'current', label: t('billing.currentPeriod') },
              { value: 'last', label: t('billing.lastMonth') },
              { value: 'quarter', label: t('billing.thisQuarter') },
              { value: 'year', label: t('billing.thisYear') },
            ]}
            className="w-52"
          />
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <span className="material-icons animate-spin text-4xl mb-2 text-gray-400">refresh</span>
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </CardContent>
        </Card>
      ) : summary ? (
        <>
          {/* Usage Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t('billing.callMinutes')}
                  </span>
                  <span className="material-icons text-gray-400 dark:text-gray-500">phone</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {formatCallDuration(summary.by_type.call?.quantity ?? 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('billing.cost')}: {formatCost(summary.by_type.call?.cost ?? '0.00')}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t('billing.sms')}
                  </span>
                  <span className="material-icons text-gray-400 dark:text-gray-500">message</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {Math.floor(summary.by_type.sms?.quantity ?? 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('billing.cost')}: {formatCost(summary.by_type.sms?.cost ?? '0.00')}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t('billing.webSearch')}
                  </span>
                  <span className="material-icons text-gray-400 dark:text-gray-500">search</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {Math.floor(summary.by_type.web_search?.quantity ?? 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('billing.cost')}: {formatCost(summary.by_type.web_search?.cost ?? '0.00')}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t('billing.phoneNumbers')}
                  </span>
                  <span className="material-icons text-gray-400 dark:text-gray-500">dialpad</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {Math.floor(summary.by_type.phone_number?.quantity ?? 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('billing.cost')}: {formatCost(summary.by_type.phone_number?.cost ?? '0.00')}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Total Cost Card */}
          <Card>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {t('billing.totalCost')}
                  </div>
                  <div className="text-4xl font-bold text-gray-900 dark:text-white">
                    {formatCost(summary.total_usage ?? '0.00')}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {summary.period?.start && summary.period?.end && (
                      <>
                        Period: {formatPeriodDate(summary.period.start)} -{' '}
                        {formatPeriodDate(summary.period.end)}
                      </>
                    )}
                  </div>
                </div>
                <span className="material-icons text-6xl text-primary-500 opacity-20">
                  payments
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Usage Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardContent>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Usage Over Time
                </h3>
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="currentColor"
                        className="opacity-20"
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: 'currentColor' }}
                        className="text-gray-600 dark:text-gray-400"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis
                        tick={{ fill: 'currentColor' }}
                        className="text-gray-600 dark:text-gray-400"
                        style={{ fontSize: '12px' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgb(17, 24, 39)', // dark gray-900
                          border: '2px solid rgb(59, 130, 246)', // blue border
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow:
                            '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                        }}
                        labelStyle={{
                          color: 'rgb(255, 255, 255)',
                          fontWeight: '600',
                          marginBottom: '8px',
                        }}
                        itemStyle={{
                          color: 'rgb(255, 255, 255)',
                        }}
                        formatter={(value: number, name: string) => {
                          const formattedValue = value > 0 ? formatCost(value.toFixed(2)) : '€0.00';
                          const labelMap: Record<string, string> = {
                            call_minutes: t('billing.callMinutes'),
                            sms: t('billing.sms'),
                            web_search: t('billing.webSearch'),
                            phone_number: t('billing.phoneNumbers'),
                          };
                          return [formattedValue, labelMap[name] || name];
                        }}
                      />
                      <Bar
                        dataKey="call_minutes"
                        stackId="a"
                        fill="#3b82f6"
                        name={t('billing.callMinutes')}
                      />
                      <Bar dataKey="sms" stackId="a" fill="#10b981" name={t('billing.sms')} />
                      <Bar
                        dataKey="web_search"
                        stackId="a"
                        fill="#f59e0b"
                        name={t('billing.webSearch')}
                      />
                      <Bar
                        dataKey="phone_number"
                        stackId="a"
                        fill="#8b5cf6"
                        name={t('billing.phoneNumbers')}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <span className="material-icons text-4xl mb-2 text-gray-400">bar_chart</span>
            <p className="text-gray-500 dark:text-gray-400">{t('billing.noUsageData')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
